
import { AuditInstructionFileModel, FileModel } from '@/libs/Api';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import Page from '@/Page.vue';
import axios from 'axios';
import { getAccessToken } from 'axios-jwt';
import { Component, VModel, Watch } from 'vue-property-decorator';
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';

@Component({
    components: { VuePdfEmbed },
})
export default class InstructionFilePreview extends Page {
    blobUrls: string[] = [];
    loadingFiles: boolean[] = [];

    // Dialog states
    imageDialog = false;
    pdfDialog = false;
    currentFile: AuditInstructionFileModel | null = null;
    currentFileIndex = -1;
    currentBlobUrl = '';

    // Image zoom states
    imageZoom = 1;
    panX = 0;
    panY = 0;
    isPanning = false;
    startX = 0;
    startY = 0;
    lastPanX = 0;
    lastPanY = 0;

    // Touch states
    lastTouchDistance = 0;
    initialPinchZoom = 1;

    @VModel() files!: AuditInstructionFileModel[];

    isImageFile(file: FileModel | null | undefined): boolean {
        const imageTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/heic'];
        return imageTypes.some((type) => type === file?.contentType);
    }

    isPdfFile(file: FileModel | null | undefined): boolean {
        const pdfTypes = ['application/pdf'];
        return pdfTypes.some((type) => type === file?.contentType);
    }

    isOtherFile(file: FileModel | null | undefined): boolean {
        return !this.isImageFile(file) && !this.isPdfFile(file);
    }

    getFileTypeLabel(file: FileModel | null | undefined): string {
        if (this.isImageFile(file)) return this.$t('files.viewFile').toString();
        if (this.isPdfFile(file)) return 'PDF';
        return this.$t('common.downloadFile').toString();
    }

    mounted() {
        this.loadAllFiles();
    }

    @Watch('files', { deep: true })
    onFilesChanged() {
        this.loadAllFiles();
    }

    loadAllFiles() {
        if (!this.files || this.files.length === 0) {
            this.blobUrls = [];
            this.loadingFiles = [];
            return;
        }

        // Inicializace polí
        this.blobUrls = new Array(this.files.length).fill('');
        this.loadingFiles = new Array(this.files.length).fill(false);

        // Načtení všech souborů
        this.files.forEach((fileItem, index) => {
            if (fileItem.file) {
                this.loadFile(fileItem.file, index);
            }
        });
    }

    loadFile(file: FileModel, index: number) {
        if (!file || !file.id) return;

        this.$set(this.loadingFiles, index, true);
        const tenantId = authStore.useGetter(AuthGetter.GetTenant);
        const token = getAccessToken();

        axios({
            url: (window as any).ApiService.baseUrl + '/api/files/' + file.id! + '/download',
            method: 'GET',
            responseType: 'blob',
            headers: {
                TenantId: tenantId,
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => {
                this.$set(this.loadingFiles, index, false);
                const url = window.URL.createObjectURL(new Blob([response.data], { type: file.contentType! }));
                this.$set(this.blobUrls, index, url);
            })
            .catch(() => {
                this.$set(this.loadingFiles, index, false);
            });
    }

    handleFileClick(fileItem: AuditInstructionFileModel, index: number) {
        if (!this.blobUrls[index]) return;

        this.currentFile = fileItem;
        this.currentFileIndex = index;
        this.currentBlobUrl = this.blobUrls[index];

        if (this.isImageFile(fileItem.file)) {
            this.openImageDialog();
        } else if (this.isPdfFile(fileItem.file)) {
            this.openPdfDialog();
        } else {
            this.downloadFileByIndex(index);
        }
    }

    // Image dialog methods
    openImageDialog() {
        this.imageDialog = true;
        this.resetZoom();
    }

    closeImageDialog() {
        this.imageDialog = false;
        this.currentFile = null;
        this.currentFileIndex = -1;
        this.currentBlobUrl = '';
    }

    zoomIn() {
        if (this.imageZoom < 3) {
            this.imageZoom = Math.min(this.imageZoom + 0.25, 3);
        }
    }

    zoomOut() {
        if (this.imageZoom > 0.5) {
            this.imageZoom = Math.max(this.imageZoom - 0.25, 0.5);
            // Adjust pan when zooming out to prevent image from moving too far
            this.adjustPanLimits();
        }
    }

    resetZoom() {
        this.imageZoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.lastPanX = 0;
        this.lastPanY = 0;
    }

    handleWheel(event: WheelEvent) {
        event.preventDefault();
        const delta = -event.deltaY / 1000;
        const newZoom = Math.max(0.5, Math.min(3, this.imageZoom + delta));
        this.imageZoom = newZoom;
        this.adjustPanLimits();
    }

    startPan(event: MouseEvent) {
        if (this.imageZoom > 1) {
            this.isPanning = true;
            this.startX = event.clientX - this.lastPanX;
            this.startY = event.clientY - this.lastPanY;
        }
    }

    pan(event: MouseEvent) {
        if (this.isPanning) {
            this.panX = (event.clientX - this.startX) / this.imageZoom;
            this.panY = (event.clientY - this.startY) / this.imageZoom;
        }
    }

    endPan() {
        if (this.isPanning) {
            this.isPanning = false;
            this.lastPanX = this.panX * this.imageZoom;
            this.lastPanY = this.panY * this.imageZoom;
            this.adjustPanLimits();
        }
    }

    adjustPanLimits() {
        // Keep the image centered when zooming out
        if (this.imageZoom <= 1) {
            this.panX = 0;
            this.panY = 0;
            this.lastPanX = 0;
            this.lastPanY = 0;
        }
    }

    // Touch gestures
    handleTouchStart(event: TouchEvent) {
        if (event.touches.length === 2) {
            // Pinch to zoom
            this.lastTouchDistance = this.getTouchDistance(event.touches);
            this.initialPinchZoom = this.imageZoom;
        } else if (event.touches.length === 1 && this.imageZoom > 1) {
            // Pan
            this.isPanning = true;
            this.startX = event.touches[0].clientX - this.lastPanX;
            this.startY = event.touches[0].clientY - this.lastPanY;
        }
    }

    handleTouchMove(event: TouchEvent) {
        event.preventDefault();

        if (event.touches.length === 2) {
            // Pinch to zoom
            const currentDistance = this.getTouchDistance(event.touches);
            const scale = currentDistance / this.lastTouchDistance;
            this.imageZoom = Math.max(0.5, Math.min(3, this.initialPinchZoom * scale));
            this.adjustPanLimits();
        } else if (event.touches.length === 1 && this.isPanning) {
            // Pan
            this.panX = (event.touches[0].clientX - this.startX) / this.imageZoom;
            this.panY = (event.touches[0].clientY - this.startY) / this.imageZoom;
        }
    }

    handleTouchEnd(event: TouchEvent) {
        if (event.touches.length === 0) {
            this.isPanning = false;
            this.lastPanX = this.panX * this.imageZoom;
            this.lastPanY = this.panY * this.imageZoom;
            this.adjustPanLimits();
        }
    }

    getTouchDistance(touches: TouchList): number {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    // PDF dialog methods
    openPdfDialog() {
        this.pdfDialog = true;
    }

    closePdfDialog() {
        this.pdfDialog = false;
        this.currentFile = null;
        this.currentFileIndex = -1;
        this.currentBlobUrl = '';
    }

    // Download methods
    downloadCurrentFile() {
        if (this.currentFileIndex >= 0) {
            this.downloadFileByIndex(this.currentFileIndex);
        }
    }

    downloadFileByIndex(index: number) {
        const fileItem = this.files[index];
        const blobUrl = this.blobUrls[index];

        if (fileItem && fileItem.file && blobUrl) {
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileItem.file.fileName || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    beforeDestroy() {
        // Clean up blob URLs
        this.blobUrls.forEach((url) => {
            if (url) {
                window.URL.revokeObjectURL(url);
            }
        });
    }
}
